export type CookieValueTypes = string | undefined;

export type CookieSetOptions = {
  path?: string;
  maxAge?: number;
  sameSite?: "strict" | "lax" | "none" | boolean;
  secure?: boolean;
  domain?: string;
  expires?: Date;
};

function readDocumentCookie(): string {
  if (typeof document === "undefined") return "";
  return document.cookie;
}

export function getCookie(name: string): CookieValueTypes {
  const cookies = readDocumentCookie();
  if (!cookies) return undefined;

  const parts = cookies.split("; ");
  for (const part of parts) {
    const eq = part.indexOf("=");
    const key = eq >= 0 ? part.slice(0, eq) : part;
    if (key !== name) continue;
    const raw = eq >= 0 ? part.slice(eq + 1) : "";
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  return undefined;
}

export function setCookie(
  name: string,
  value: string | number | boolean,
  options: CookieSetOptions = {}
): void {
  if (typeof document === "undefined") return;

  const path = options.path ?? "/";
  let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(String(value))}; path=${path}`;

  if (options.maxAge != null) {
    cookie += `; max-age=${Math.floor(options.maxAge)}`;
  }
  if (options.expires) {
    cookie += `; expires=${options.expires.toUTCString()}`;
  }
  if (options.domain) {
    cookie += `; domain=${options.domain}`;
  }
  if (options.sameSite != null) {
    const sameSite =
      typeof options.sameSite === "boolean"
        ? options.sameSite
          ? "strict"
          : undefined
        : options.sameSite;
    if (sameSite) {
      cookie += `; samesite=${sameSite}`;
    }
  }
  if (options.secure) {
    cookie += "; secure";
  }

  document.cookie = cookie;
}

export function deleteCookie(name: string, options: Pick<CookieSetOptions, "path" | "domain"> = {}) {
  setCookie(name, "", {
    ...options,
    path: options.path ?? "/",
    maxAge: 0,
    expires: new Date(0)
  });
}
